import { Suspense } from 'react';
import { lazyWithRetry as lazy } from '@/utils/lazy-with-retry';
import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { LoadingState } from '@/components/ui/loading-state';

function EstimatesDetailsLegacyRedirect() {
  const [params] = useSearchParams();
  const id = params.get('id');
  return <Navigate to={id ? `/projects/${id}/estimate` : '/estimates'} replace />;
}

// Static imports (small/needed immediately)
import SignupPage from '@/pages/SignupPage';
import LoginPage from '@/pages/LoginPage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import NotFoundPage from '@/pages/NotFoundPage';
import DesignPreviewRedirect from '@/pages/DesignPreviewRedirect';
import ProtectedRoute from '@/components/ProtectedRoute';
import PublicRoute from '@/components/PublicRoute';
import { PERMISSIONS } from '@/constants/permissions';
import { canAccessBookkeeperWork } from '@/lib/bookkeeper-access';
import { isCompanyOwner } from '@/lib/owner-access';
import { transactionsHomeHref } from '@/features/financial/payableHome';
import { getFeatureFlag } from '@/lib/feature-flags';
import { OnboardingStatusProvider } from '@/contexts/OnboardingStatusContext';
import ContractPage from '@/pages/ContractPage';
import LegacyProposalRedirect from '@/pages/LegacyProposalRedirect';
import ProposalPublicView from '@/pages/ProposalPublicView';
import ClientContractPage from '@/pages/ClientContractPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

// Onboarding step imports (kept static)
import Step1CompanyDetails from '@/pages/onboarding/Step1CompanyDetails';
import Step2TeamInformation from '@/pages/onboarding/Step2TeamInformation';
import Step3CompanyConfiguration from '@/pages/onboarding/Step3CompanyConfiguration';
import Step4YourElements from '@/pages/onboarding/Step4YourElements';
import Step5ReviewFinish from '@/pages/onboarding/Step5ReviewFinish';

// Extended onboarding for architect/subcontractor (kept static)
import BusinessInfoStep from '@/pages/onboarding/BusinessInfoStep';
import ClassificationStep from '@/pages/onboarding/ClassificationStep';
import ContactInfoStep from '@/pages/onboarding/ContactInfoStep';
import DocumentsStep from '@/pages/onboarding/DocumentsStep';
import AgreementsStep from '@/pages/onboarding/AgreementsStep';
import AddUsersStep from '@/pages/onboarding/AddUsersStep';
import ReviewStep from '@/pages/onboarding/ReviewStep';
import OnboardingSuccess from '@/pages/onboarding/OnboardingSuccess';

// Lazy-loaded page imports (heavy pages, code-split for performance)
const DesignSystemCatalog = lazy(() => import('@/pages/DesignSystemCatalog'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const OwnerHomePage = lazy(() => import('@/pages/OwnerHomePage'));
const ClientsPage = lazy(() => import('@/pages/ClientsPage'));
const AddClientPage = lazy(() => import('@/pages/AddClientPage'));
const ClientDetailPage = lazy(() => import('@/pages/ClientDetailPage'));
const ClientMergePage = lazy(() => import('@/pages/ClientMergePage'));
const LeadsPage = lazy(() => import('@/pages/LeadsPage'));
const LeadDetailPage = lazy(() => import('@/pages/LeadDetailPage'));
const CreateLeadPage = lazy(() => import('@/pages/CreateLeadPage'));
const EditLeadPage = lazy(() => import('@/pages/EditLeadPage'));
const TeamsPage = lazy(() => import('@/pages/TeamsPage'));
const TeamMemberDetailsPage = lazy(() => import('@/pages/TeamMemberDetailsPage'));
const EstimatesPage = lazy(() => import('@/pages/EstimatesPage'));
const ProposalsPage = lazy(() => import('@/pages/ProposalsPage'));
const ContractsListPage = lazy(() => import('@/pages/ContractsListPage'));
// /proposals is a real list of finalized estimates (ready to present).
// Legacy /sales-pipeline still lands here.
const IntakeFormsPage = lazy(() => import('@/pages/IntakeFormsPage'));
// IntakeForm (legacy multi-step intake) deleted — /intake-form now
// redirects via LegacyIntakeFormRedirect.
const EstimatePage = lazy(() => import('@/pages/EstimatePage'));
const PublicSignPage = lazy(() => import('@/pages/PublicSignPage'));
// Public legal pages — required by Google OAuth verification (hub LarkPM1/C-LarkPM#58).
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/legal/TermsOfServicePage'));
const NewIntakePage = lazy(() => import('@/pages/NewIntakePage'));
const IntakeElementPickerPage = lazy(() => import('@/pages/IntakeElementPickerPage'));
const IntakeSummaryPage = lazy(() => import('@/pages/IntakeSummaryPage'));
const SalesRepDashboard = lazy(() => import('@/pages/SalesRepDashboard'));
const ServiceRequestsPage = lazy(() => import('@/pages/ServiceRequestsPage'));
const ServiceRequestDetailPage = lazy(() => import('@/pages/ServiceRequestDetailPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ActivityHub = lazy(() => import('@/pages/ActivityHub'));
const CorrespondencePage = lazy(() => import('@/pages/CorrespondencePage'));

// Procurement & Cost Viewer pages
const ReviewQueuePage = lazy(() => import('@/pages/procurement/ReviewQueuePage'));
const FinancialTransactionsPage = lazy(() => import('@/pages/FinancialTransactionsPage'));
const FinancialSkuMappingsPage = lazy(() => import('@/pages/FinancialSkuMappingsPage'));
const SupplierDetailPage = lazy(() => import('@/pages/SupplierDetailPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/InvoiceDetailPage'));
// Marketing module v1 — flag-gated alongside its nav entry.
const MarketingSurface = lazy(() => import('@/features/marketing/MarketingSurface'));

// Rewards pages (Sprint 43)
const RewardsPage = lazy(() => import('@/pages/RewardsPage'));
const RewardInvoiceDetailPage = lazy(() => import('@/pages/rewards/RewardInvoiceDetailPage'));
const RewardVendorDetailPage = lazy(() => import('@/pages/rewards/RewardVendorDetailPage'));

// Partner Hub pages
// Sprint 48 — PartnerDetailPage is no longer routed standalone; it renders
// embedded inside PartnersPage (split master–detail). The /partners/details
// route redirects to /partners?id= to preserve existing deep links.
const PartnersPage = lazy(() => import('@/pages/PartnersPage'));
const AddPartnerPage = lazy(() => import('@/pages/AddPartnerPage'));

// Products & Services pages
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const PricingTablesPage = lazy(() => import('@/pages/PricingTablesPage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const AdminOverviewPage = lazy(() => import('@/pages/AdminOverviewPage'));
const ProjectBudgetPage = lazy(() => import('@/pages/ProjectBudgetPage'));
const BudgetSectionPage = lazy(() => import('@/pages/BudgetSectionPage'));
const ProjectsListPage = lazy(() => import('@/pages/ProjectsListPage'));
const ProjectMergePage = lazy(() => import('@/pages/ProjectMergePage'));
const ProjectDetailPage = lazy(() => import('@/pages/ProjectDetailPage'));
const CreateProjectPage = lazy(() => import('@/pages/CreateProjectPage'));
const CreateExistingProjectPage = lazy(() => import('@/pages/CreateExistingProjectPage'));
const ProjectImportPage = lazy(() => import('@/pages/ProjectImportPage'));
const SubHubPage = lazy(() => import('@/pages/SubHubPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));

// Tasks v2 — Slice 1.4 (Phase 1 canonical Tasks module).
// `/operations/project-fulfillment` remains the legacy ProjectManagement
// surface (Slice 1.0); `/tasks` is the NEW person-to-person module.
const TasksListPage = lazy(() => import('@/pages/tasks/TasksListPage'));
const TaskDetailPage = lazy(() => import('@/pages/tasks/TaskDetailPage'));
const WeeklyReportEditorPage = lazy(
  () => import('@/pages/weekly-reports/WeeklyReportEditorPage'),
);
const WeeklyReportsOwnerPage = lazy(
  () => import('@/pages/weekly-reports/WeeklyReportsOwnerPage'),
);

/**
 * Legacy `/intake-form` route handler. Reads `?pid=` from the URL and
 * sends the user to the new element-picker for that project; falls
 * back to the intake-forms list if no pid is present. Keeps stale
 * bookmarks + pre-cutover chat notification deep links functional.
 */
function LegacyIntakeFormRedirect() {
  const [params] = useSearchParams();
  const pid = params.get('pid');
  return <Navigate to={pid ? `/projects/${pid}/intake` : '/intake-forms'} replace />;
}

/**
 * Bug 7 (2026-08-05) — the estimator review flow is retired. Old
 * /estimator/review/:projectId deep links (chat notifications, emails)
 * land on the estimate page, whose first open claims the estimate.
 */
function EstimatorReviewRedirect() {
  const { projectId } = useParams();
  return (
    <Navigate
      to={projectId ? `/projects/${projectId}/estimate` : '/dashboard'}
      replace
    />
  );
}

/**
 * Sprint 48 — the standalone partner detail route is retired in favour of
 * the split master–detail Partners page. Preserve `?id=`/`?tab=` deep links
 * by redirecting into `/partners` with the same params.
 */
function PartnerDetailsLegacyRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={qs ? `/partners?${qs}` : '/partners'} replace />;
}

/** Old Procurement list home → Transactions ledger (keep projectId / filters). */
function ProcurementHomeRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={transactionsHomeHref(params)} replace />;
}

/** Legacy Review Queue "View mapping" links → Money SKU mappings workspace. */
function ProcurementSkuMappingsRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return (
    <Navigate
      to={qs ? `/financial/sku-mappings?${qs}` : '/financial/sku-mappings'}
      replace
    />
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingState message="Loading page..." /></div>}>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/sign-up"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Contract page - internal (uses JWT for full contract editing/signing) */}
      <Route
        path="/contract/:contractId?"
        element={
          <ProtectedRoute permission={PERMISSIONS.CONTRACT.VIEW}>
            <ContractPage />
          </ProtectedRoute>
        }
      />

      {/* Client contract page - public (no auth, for client viewing/signing) */}
      <Route path="/client/contract/:contractId" element={<ClientContractPage />} />

      {/* Sprint 41 W35-cutover — legacy `/proposal/:proposalId` URLs
          redirect through the W35-A backend mapping to the new
          snapshot-backed `/p/:token` flow. ClientProposalPage was
          deleted in the same wave. */}
      <Route path="/proposal/:proposalId" element={<LegacyProposalRedirect />} />

      {/* Sprint 41 W34 — public proposal view backed by EstimateSnapshot. Token IS the credential; no auth, no MainLayout. */}
      <Route path="/p/:token" element={<ProposalPublicView />} />

      {/* Public sign page — bound to a one-time JWT token (M8). */}
      <Route path="/sign/:token" element={<PublicSignPage />} />

      {/* Public legal pages — no auth, viewable signed in or out. */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-invite"
        element={
          <PublicRoute>
            <AcceptInvitePage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/owner"
        element={
          <ProtectedRoute authorize={isCompanyOwner} permissionFallback="/dashboard">
            <OwnerHomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-dashboard"
        element={
          <ProtectedRoute>
            <SalesRepDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-requests"
        element={
          <ProtectedRoute>
            <ServiceRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-requests/detail"
        element={
          <ProtectedRoute>
            <ServiceRequestDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <ProtectedRoute>
            <ContractsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/add"
        element={
          <ProtectedRoute permission={PERMISSIONS.CLIENT.CREATE} permissionFallback="/clients">
            <AddClientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/edit"
        element={
          <ProtectedRoute permission={PERMISSIONS.CLIENT.DETAILS_EDIT} permissionFallback="/clients">
            <AddClientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/details"
        element={
          <ProtectedRoute>
            <ClientDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/merge"
        element={
          <ProtectedRoute
            permissions={[PERMISSIONS.CLIENT.DETAILS_EDIT, PERMISSIONS.CLIENT.DELETE]}
            requireAll
            permissionFallback="/clients"
          >
            <ClientMergePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-hub"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <Navigate to="/activity-hub/company-chat" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-hub/company-chat"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <ActivityHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-hub/log-sales-activity"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <ActivityHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-hub/project-chat"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <ActivityHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-hub/correspondence"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <Navigate to="/correspondence" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/correspondence"
        element={
          <ProtectedRoute permission={PERMISSIONS.CORRESPONDENCE.VIEW}>
            <CorrespondencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/details"
        element={
          <ProtectedRoute>
            <LeadDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/create"
        element={
          <ProtectedRoute>
            <CreateLeadPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/edit"
        element={
          <ProtectedRoute permission={PERMISSIONS.LEAD.EDIT} permissionFallback="/leads">
            <EditLeadPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <TeamsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/details"
        element={
          <ProtectedRoute>
            <TeamMemberDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/projects" element={<Navigate to="/project-hub/projects" replace />} />
      <Route
        path="/estimates"
        element={
          <ProtectedRoute permission={PERMISSIONS.ESTIMATE.READ}>
            <EstimatesPage />
          </ProtectedRoute>
        }
      />
      {/* Sales-pipeline → Clients consolidation (post-Wave-5).
          The standalone /sales-pipeline page is replaced by the client
          pipelineStatus filter on /clients. These redirects preserve any
          saved dashboard deep-links and legacy /proposals bookmarks. */}
      <Route path="/sales-pipeline" element={<Navigate to="/proposals" replace />} />
      <Route
        path="/proposals"
        element={
          <ProtectedRoute permission={PERMISSIONS.PROPOSAL.VIEW}>
            <ProposalsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/proposals/details" element={<Navigate to="/proposals" replace />} />
      <Route path="/proposals/edit" element={<Navigate to="/proposals" replace />} />
      <Route
        path="/intake-forms"
        element={
          // 2026-07-22 bug batch #5 — auth alone let any signed-in user
          // open the intake table; gate on the intake-view permission,
          // matching how /estimates gates on ESTIMATE.READ.
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <IntakeFormsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake-forms/new"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.CREATE}>
            <CreateProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake-forms/:intakeFormId/setup"
        element={
          <ProtectedRoute>
            <CreateProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake-forms/:intakeFormId/intake"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <IntakeElementPickerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake-forms/:intakeFormId/intake-summary"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <IntakeSummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake-forms/:intakeFormId/intake/:elementCode"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <NewIntakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/estimates/details"
        element={<EstimatesDetailsLegacyRedirect />}
      />
      <Route
        path="/projects/:projectId/estimate"
        element={
          <ProtectedRoute permission={PERMISSIONS.ESTIMATE.READ}>
            <EstimatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/intake"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <IntakeElementPickerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/intake-summary"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <IntakeSummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/setup"
        element={
          <ProtectedRoute>
            <CreateProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/intake/:elementCode"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.VIEW}>
            <NewIntakePage />
          </ProtectedRoute>
        }
      />
      {/* Sprint 41 ERS — legacy queue retired in favor of the Review
          Queue table on the dashboard + the new Evaluation Screen.
          One-sprint redirect; the route + page can be deleted in
          Sprint 42 once nothing links here. */}
      <Route
        path="/intake-form/pending"
        element={<Navigate to="/dashboard" replace />}
      />
      {/* Bug 7 (2026-08-05): the estimator review flow is retired — the
          estimator's first open of /projects/:id/estimate claims the
          estimate. Old deep links land on the estimate page. */}
      <Route
        path="/estimator/review/:projectId"
        element={<EstimatorReviewRedirect />}
      />

      {/* Onboarding routes - Public routes for new users */}
      {/* Company onboarding (5 steps) - wrapped with status provider */}
      <Route
        path="/onboarding/profile"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <Step1CompanyDetails />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/teamMembers"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <Step2TeamInformation />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/configuration"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <Step3CompanyConfiguration />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/elements"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <Step4YourElements />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/complete"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <Step5ReviewFinish />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />

      {/* Extended onboarding for architect/subcontractor (7 steps) - wrapped with status provider */}
      <Route
        path="/onboarding/business-info"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <BusinessInfoStep key="business-info" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/classification"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <ClassificationStep key="classification" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/contact-info"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <ContactInfoStep key="contact-info" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/documents"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <DocumentsStep key="documents" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/agreements"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <AgreementsStep key="agreements" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/add-users"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <AddUsersStep key="add-users" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding/review"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <ReviewStep key="review" />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      {/* Common onboarding routes */}
      <Route
        path="/onboarding/success"
        element={
          <PublicRoute>
            <OnboardingStatusProvider>
              <OnboardingSuccess />
            </OnboardingStatusProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding"
        element={<Navigate to="/onboarding/profile" replace />}
      />

      {/* Legacy intake-form route — replaced by the per-element flow
          (/projects/:id/intake/:elementCode). Smart redirect catches any
          stale bookmarks / pre-cutover chat notification deep links:
          ?pid → land on the element picker for that project; otherwise
          dump the user at the intake-forms list. */}
      <Route
        path="/intake-form"
        element={
          <ProtectedRoute>
            <LegacyIntakeFormRedirect />
          </ProtectedRoute>
        }
      />

      {/* Sub Hub */}
      <Route
        path="/sub-hub"
        element={
          <ProtectedRoute>
            <SubHubPage />
          </ProtectedRoute>
        }
      />

      {/* Sprint 38 Workflow IA routes — CATALOG section */}
      <Route
        path="/catalog/products"
        element={
          <ProtectedRoute>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/pricing-tables"
        element={
          <ProtectedRoute
            permission={PERMISSIONS.PRICING_TABLES.VIEW}
            permissionFallback="/catalog/products"
          >
            <PricingTablesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/services"
        element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/overview"
        element={
          <ProtectedRoute>
            <AdminOverviewPage />
          </ProtectedRoute>
        }
      />

      {/* Sprint 38 — One-release legacy redirects from /financial-hub/* paths.
          Preserves bookmarks and shared deep links. Plan: remove next sprint
          after analytics confirms zero traffic on the old paths. */}
      <Route path="/financial-hub/products" element={<Navigate to="/catalog/products" replace />} />
      <Route path="/financial-hub/services" element={<Navigate to="/catalog/services" replace />} />
      <Route path="/financial-hub/overview" element={<Navigate to="/catalog/overview" replace />} />
      {/* ProdTRed UI retired (governance: retire prodtred stack). */}
      <Route path="/catalog/prod-t-red" element={<Navigate to="/catalog/products" replace />} />

      {/* Tasks unification: the legacy contract-fulfillment dashboard
          (ProjectManagementPage at /operations/project-fulfillment) is retired.
          Project fulfillment is now the unified Tasks module — the per-project
          view lives on Project Detail → "Project Fulfillment" tab and the
          org-wide view at /tasks. These paths redirect so deep links resolve. */}
      <Route
        path="/operations/project-fulfillment"
        element={<Navigate to="/tasks" replace />}
      />
      <Route
        path="/activity-hub/tasks"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <Navigate to="/tasks" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-hub/tasks/*"
        element={
          <ProtectedRoute permission={PERMISSIONS.ACTIVITY_HUB.VIEW}>
            <Navigate to="/tasks" replace />
          </ProtectedRoute>
        }
      />

      {/* Tasks v2 Phase 1 — Slice 1.4:
          Canonical Tasks module. List page hosts Owner / Supervisor /
          Self tasks. `KAI` source is frozen leftover (`[LARK-KAI-1]`).
          The detail route renders the Stacked Worksheet layout
          with subtasks + audit history. Per-task chat panel is feature-flagged
          via `tasksV2Messaging` until BE Slice 1.2 ships. See
          docs/design-proposals/2026-05-22-tasks-v2-and-weekly-report.md §9.1. */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TasksListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/weekly-reports/owner"
        element={
          <ProtectedRoute>
            <WeeklyReportsOwnerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/weekly-reports/mine"
        element={
          <ProtectedRoute>
            <WeeklyReportEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/weekly-reports/:reportId"
        element={
          <ProtectedRoute>
            <WeeklyReportEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/:taskId"
        element={
          <ProtectedRoute>
            <TaskDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/budget/:section"
        element={
          <ProtectedRoute>
            <BudgetSectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/budget"
        element={
          <ProtectedRoute>
            <ProjectBudgetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-hub/projects"
        element={
          <ProtectedRoute>
            <ProjectsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/merge"
        element={
          <ProtectedRoute
            permission={PERMISSIONS.PROJECT.MERGE}
            permissionFallback="/project-hub/projects"
          >
            <ProjectMergePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/new"
        element={
          <ProtectedRoute permission={PERMISSIONS.INTAKE.CREATE}>
            <CreateProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/existing"
        element={
          <ProtectedRoute>
            <CreateExistingProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/import"
        element={
          <ProtectedRoute>
            <ProjectImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/details"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-hub/budgets"
        element={<Navigate to="/project-hub/projects" replace />}
      />

      {/* Financial — Transactions is the only payable home. /procurement and
          /financial/procurement redirect here (keep projectId / ledger filters).
          Invoice detail stays on existing /procurement/invoices URLs. */}
      <Route
        path="/financial"
        element={<Navigate to="/financial/transactions" replace />}
      />
      <Route
        path="/financial/transactions"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <FinancialTransactionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial/sku-mappings"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <FinancialSkuMappingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial/procurement"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <ProcurementHomeRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial/procurement/*"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <ProcurementHomeRedirect />
          </ProtectedRoute>
        }
      />

      {/* Procurement row-detail + leftover subpages. List home is Transactions. */}
      <Route
        path="/procurement/sku-mappings"
        element={<ProcurementSkuMappingsRedirect />}
      />
      <Route
        path="/procurement/review-queue"
        element={
          <ProtectedRoute permission={PERMISSIONS.PROCUREMENT.VIEW}>
            <ReviewQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/invoices/details"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <InvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/invoices/:id"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <InvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/suppliers/details"
        element={
          <ProtectedRoute permission={PERMISSIONS.PROCUREMENT.VIEW}>
            <SupplierDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement"
        element={
          <ProtectedRoute authorize={canAccessBookkeeperWork}>
            <ProcurementHomeRedirect />
          </ProtectedRoute>
        }
      />

      {/* Rewards routes (Sprint 43) — most-specific first */}
      <Route
        path="/rewards/invoices/details"
        element={
          <ProtectedRoute>
            <RewardInvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards/invoices/:id"
        element={
          <ProtectedRoute>
            <RewardInvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards/vendors/details"
        element={
          <ProtectedRoute>
            <RewardVendorDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        }
      />

      {/* Marketing module (flag-gated with its nav entry) */}
      {getFeatureFlag('marketingModule') && (
        <Route
          path="/marketing"
          element={
            <ProtectedRoute permission={PERMISSIONS.MARKETING.VIEW}>
              <MarketingSurface />
            </ProtectedRoute>
          }
        />
      )}

      {/* Partner Hub routes */}
      <Route
        path="/partners"
        element={
          <ProtectedRoute>
            <PartnersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners/add"
        element={
          <ProtectedRoute>
            <AddPartnerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners/edit"
        element={
          <ProtectedRoute>
            <AddPartnerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners/details"
        element={
          <ProtectedRoute>
            <PartnerDetailsLegacyRedirect />
          </ProtectedRoute>
        }
      />

      {/* Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      {/* Design-system primitive catalog (dev/QA only — used by the
          Playwright tier-2 visual regression suite). Not gated behind
          ProtectedRoute so screenshot runs don't require auth setup. */}
      <Route path="/design-system" element={<DesignSystemCatalog />} />
      <Route path="/design-system/:primitive" element={<DesignSystemCatalog />} />

      {/* Owned staging catalog (governance 20). CloudFront SPA-fallback
          otherwise shows NotFound for /previews/. */}
      <Route path="/previews" element={<DesignPreviewRedirect />} />
      <Route path="/previews/*" element={<DesignPreviewRedirect />} />

      {/* 404 routes */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
